import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { levelStyle, fmtStudy } from "@/lib/tcfLevel";
import { useI18n } from "@/context/I18nContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import {
  BookOpen, Headphones, CheckCircle2, Clock, TrendingUp, Award, Target,
  ArrowRight, Lock, Crown, Loader2, ArrowLeft,
} from "lucide-react";

const SKILLS = [
  { key: "reading", icon: BookOpen, en: "Reading", fr: "Écrite", frLong: "compréhension écrite" },
  { key: "listening", icon: Headphones, en: "Listening", fr: "Orale", frLong: "compréhension orale" },
];

const STR = {
  en: {
    progress: "Progress", quizzesDone: "Quizzes done", complete: "complete", studyTime: "Study time",
    acrossAttempts: "across all attempts", attempts: "Attempts", totalSubs: "total submissions",
    successRate: "Success rate", avgAccuracy: "avg. accuracy", tcfLevel: "TCF level", avg: "avg.",
    noAttempts: "no attempts yet", allQuizzes: "All quizzes", completed: "completed",
    all: "All", done: "Completed", todo: "Not completed", notAttempted: "Not attempted yet",
    test: "Test", noView: "No tests in this view yet.", takeToSee: "Take a test to see your level",
    view: "View", premiumTitle: "Progress tracking is a Premium feature",
    premiumSub: "Unlock all 40 Reading & Listening papers and track your official TCF level on every test.",
    goPremium: "Go Premium", allPapers: "All papers",
    trackAll: (n, s) => `Track your progress across all ${n} ${s} tests.`,
  },
  fr: {
    progress: "Progression", quizzesDone: "Quiz effectués", complete: "complété", studyTime: "Temps d'étude",
    acrossAttempts: "toutes tentatives", attempts: "Tentatives", totalSubs: "soumissions",
    successRate: "Taux de réussite", avgAccuracy: "précision moy.", tcfLevel: "Niveau TCF", avg: "moy.",
    noAttempts: "aucune tentative", allQuizzes: "Tous les quiz", completed: "complétés",
    all: "Tous", done: "Terminés", todo: "Non terminés", notAttempted: "Pas encore tenté",
    test: "Série", noView: "Aucun test dans cette vue.", takeToSee: "Passez un test pour voir votre niveau",
    view: "Voir", premiumTitle: "Le suivi de progression est réservé aux membres Premium",
    premiumSub: "Débloquez les 40 épreuves de compréhension écrite et orale et suivez votre niveau TCF officiel.",
    goPremium: "Passer Premium", allPapers: "Toutes les épreuves",
    trackAll: (n, s) => `Suivez votre progression sur les ${n} tests de ${s}.`,
  },
};

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-muted-foreground text-xs sm:text-sm font-medium">
        <span>{label}</span><Icon className="h-4 w-4 text-primary" />
      </div>
      <div className={`mt-2 font-display text-2xl sm:text-3xl font-semibold ${accent || "text-foreground"}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function SeriesCard({ t, skill, T }) {
  const pct = t.completed ? Math.round(((t.score_699 || 0) / 699) * 100) : 0;
  const st = t.completed ? levelStyle(t.cefr) : null;
  return (
    <Link to={`/exams/${t.slug}?skill=${skill}`} data-testid={`progress-card-${t.slug}`}
      className={`group relative block overflow-hidden rounded-xl border transition-all hover:shadow-md ${t.completed ? "border-border" : "border-dashed border-border"}`}>
      {t.completed && <div className={`absolute inset-y-0 left-0 ${st.fill}`} style={{ width: `${pct}%` }} />}
      <div className="relative flex items-center gap-3 p-3">
        <span className={`h-9 w-9 shrink-0 grid place-items-center rounded-full ${t.completed ? st.bar : "bg-secondary"} text-white`}>
          {t.completed ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-xs font-bold text-muted-foreground">{t.number}</span>}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm leading-tight">{T.test} {t.number}</div>
          {t.completed ? (
            <div className="text-xs text-muted-foreground truncate">
              <span className="font-semibold text-foreground">{t.label}</span> · {t.score_699}/699
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{T.notAttempted}</div>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

export default function ExamProgress() {
  const { lang } = useI18n();
  const T = STR[lang] || STR.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = ["reading", "listening"].includes(searchParams.get("skill")) ? searchParams.get("skill") : "reading";
  const [skill, setSkillState] = useState(initial);
  const setSkill = (s) => { setSkillState(s); setSearchParams({ skill: s }, { replace: true }); };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get(`/exams/progress?skill=${skill}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [skill]);

  const meta = SKILLS.find((s) => s.key === skill);
  const skillLabel = lang === "fr" ? meta.frLong : meta.en;
  const tests = data?.tests || [];
  const filtered = tests.filter((t) => filter === "all" || (filter === "done" ? t.completed : !t.completed));
  const overall = data?.overall;
  const donePct = data?.total_tests ? Math.round((data.completed / data.total_tests) * 100) : 0;

  return (
    <div data-testid="exam-progress-page">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/exams?skill=${skill}`} data-testid="back-to-bank" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft className="h-4 w-4" /> {T.allPapers}
        </Link>
        <LanguageToggle />
      </div>

      {/* Header */}
      <div className="mt-3 rounded-2xl emerald-surface grain text-white p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/15 grid place-items-center"><meta.icon className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold">{T.progress} — {lang === "fr" ? meta.fr : meta.en}</h1>
            <p className="text-white/80 text-sm">{T.trackAll(data?.total_tests || 40, skillLabel)}</p>
          </div>
        </div>
        {/* Skill toggle */}
        <div className="mt-5 inline-flex rounded-full bg-white/15 p-1" data-testid="progress-skill-tabs">
          {SKILLS.map((s) => (
            <button key={s.key} onClick={() => setSkill(s.key)} data-testid={`progress-tab-${s.key}`}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${skill === s.key ? "bg-white text-primary" : "text-white/90 hover:bg-white/10"}`}>
              <s.icon className="h-4 w-4" /> {lang === "fr" ? s.fr : s.en}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-center text-muted-foreground" data-testid="progress-loading"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : !data?.has_exam_access ? (
        <div className="mt-6 rounded-2xl bg-card border border-border p-8 text-center" data-testid="progress-premium-gate">
          <div className="h-12 w-12 rounded-2xl emerald-surface grid place-items-center mx-auto"><Lock className="h-6 w-6 text-white" /></div>
          <h2 className="mt-4 font-display text-xl font-semibold">{T.premiumTitle}</h2>
          <p className="text-muted-foreground mt-1 text-sm">{T.premiumSub}</p>
          <Link to="/premium" data-testid="progress-upgrade-btn" className="mt-5 inline-flex items-center gap-2 px-5 h-11 rounded-full bg-primary text-primary-foreground font-semibold">
            <Crown className="h-4 w-4" /> {T.goPremium}
          </Link>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard testid="stat-completed" icon={CheckCircle2} label={T.quizzesDone} value={`${data.completed}/${data.total_tests}`} sub={`${donePct}% ${T.complete}`} accent="text-primary" />
            <StatCard testid="stat-study" icon={Clock} label={T.studyTime} value={fmtStudy(data.study_seconds)} sub={T.acrossAttempts} accent="text-orange-500" />
            <StatCard testid="stat-attempts" icon={TrendingUp} label={T.attempts} value={data.total_attempts} sub={T.totalSubs} accent="text-purple-600" />
            <StatCard testid="stat-success" icon={Target} label={T.successRate} value={`${data.avg_pct}%`} sub={T.avgAccuracy} accent="text-green-600" />
            <StatCard testid="stat-level" icon={Award} label={T.tcfLevel} value={overall ? overall.label : "—"} sub={overall ? `${overall.score_699}/699 ${T.avg}` : T.noAttempts} accent="text-emerald-700" />
          </div>

          {/* Overall progress bar */}
          <div className="mt-6 rounded-2xl bg-card border border-border p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-semibold">{T.allQuizzes}</h2>
                <p className="text-sm text-muted-foreground">{data.completed}/{data.total_tests} {T.completed}</p>
              </div>
              <div className="font-display text-3xl font-semibold text-primary" data-testid="progress-overall-pct">{donePct}%</div>
            </div>
            <div className="mt-3 h-3 rounded-full bg-secondary overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${donePct}%` }} transition={{ duration: 0.6 }} className="h-full emerald-surface" />
            </div>

            {/* Filter tabs */}
            <div className="mt-5 inline-flex rounded-full bg-secondary p-1" data-testid="progress-filter">
              {[["all", T.all], ["done", T.done], ["todo", T.todo]].map(([k, lbl]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                  className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${filter === k ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
                  {lbl}
                </button>
              ))}
            </div>

            {/* Series grid */}
            {filtered.length === 0 ? (
              <p className="mt-5 text-sm text-muted-foreground text-center py-6">{T.noView}</p>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="progress-grid">
                {filtered.map((t) => <SeriesCard key={t.slug} t={t} skill={skill} T={T} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
